// @ts-nocheck - formatter / Table
import React, { useState } from 'react';
import {
  loadAccountDetailKeyRequestsForAccount,
  exportAccountDetailKeyRequestsForAccount,
} from '../../../actions/accountDetailActions';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../common/Table';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import { Link, useLocation } from 'react-router-dom';
import ImageWithFallback from '../../common/ImageWithFallback';
import FormattedDate from '../../common/FormattedDate';
import icons from '../../../constants/icons';
import Checkbox from '../../common/Checkbox';
import KebabMenu from '../../common/KebabMenu';
import Button from '../../common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { sendAccountDetailEmail } from '../../../api/requestApi';

export interface AccountDetailKeyRequestsListProps {
  id: string | number | undefined;
  currentUser?: any;
  reload?: any;
  itemsChecked: any[];
  setItemsChecked: (items: any[]) => void;
  onDisableKeys: (items: any[]) => void;
}

function AccountDetailKeyRequestsList({
  id,
  reload,
  itemsChecked,
  setItemsChecked,
  onDisableKeys,
}: AccountDetailKeyRequestsListProps) {
  const allColumns = [
    'game',
    'sku',
    'keyRequestReference',
    'emailSentDate',
    'keyCodeDownloadedDate',
  ];
  const suffixColumns = ['buttons-checkbox'];
  const defaultVisibleColumns = allColumns;
  const [accountDetails, setAccountDetails] = useState<any>();
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const { t } = useTranslation();
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
  } = useTableSettings(
    'accountDetails-keyrequests-settings',
    getTableSettingsDefaults({
      defaultSort: 'game',
      direction: 'desc',
      defaultVisibleColumns,
    }),
    { id },
    () => loadAccountDetailKeyRequestsForAccount(id),
    setAccountDetails,
    reload,
    t('Failed to load account details')
  );

  const handleResendEmail = (item: any) => {
    sendAccountDetailEmail(item.keyRequestId, item.id)
      .then(() => {
        toast.success(t('Email sent successfully'));
      })
      .catch((error: Error) => {
        toast.error(error.message || t('Failed to send email'));
      });
  };

  const columnsConfig = [
    {
      Key: 'game',
      Label: t('Game'),
      width: '40%',
      className: 'flush',
      Render: (item: any) => (
        <div className="game-img-wrapper">
          <ImageWithFallback
            url={item.packshotUrl}
            fallBackImage={
              <div className="game-img">
                <div className="icon-wrapper">
                  <FontAwesomeIcon icon={icons.GAME} className="game" />
                </div>
              </div>
            }
            alt={item.name}
          />
          <Link to={'/game/' + item.gameId}>{item.game}</Link>
        </div>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'sku',
      Label: t('SKU'),
      width: '20%',
      Render: (item: any) => <Link to={'/sku/' + item.skuId}>{item.sku}</Link>,
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyRequestReference',
      Label: t('Key Request'),
      width: '20%',
      Render: (item: any) => (
        <Link to={'/request/' + item.keyRequestGroupId}>
          {item.keyRequestReference}
        </Link>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'emailSentDate',
      Label: t('Sent Date'),
      width: '20%',
      Render: (item: any) => <FormattedDate value={item.emailSentDate} variant="default" />,
    },
    {
      Key: 'keyCodeDownloadedDate',
      Label: t('Downloaded Date'),
      width: '20%',
      Render: (item: any) => <FormattedDate value={item.keyCodeDownloadedDate} variant="default" />,
    },
    {
      Key: 'buttons-checkbox',
      Label: '',
      className: 'text-right no-navigate',
      Sort: false,
      width: '2%',
      Filter: {
        Render: (col: any) => (
          <Checkbox
            disabled={!accountDetails}
            checked={false}
            onChange={(checked) =>
              checked
                ? setItemsChecked(accountDetails?.data ?? [])
                : setItemsChecked([])
            }
          />
        ),
      },
      Render: (item: any) => (
        <div className="d-flex align-items-center justify-content-end">
          <KebabMenu
            take={1}
            className="inline-icons"
            buttons={[
              <Button
                key={item.id + 'disable'}
                className="btn btn-danger btn-xs-full me-2"
                dropdownclasses="danger"
                onClick={() => onDisableKeys([item])}
                icon={icons.DISABLE_KEYS}
                text={t('Disable keys')}
              />,
              <Button
                key={item.id + 'resend-email'}
                className="btn btn-primary btn-xs-full me-2"
                onClick={() => handleResendEmail(item)}
                icon={icons.SEND_EMAILS}
                text={t('Resend email')}
              />,
            ]}
          />
          <Checkbox
            className="ms-3"
            checked={itemsChecked?.some((x: any) => x.id === item.id)}
            onChange={(checked) =>
              checked
                ? setItemsChecked([...(itemsChecked || []), item])
                : setItemsChecked((itemsChecked || []).filter((x: any) => x.id !== item.id))
            }
          />
        </div>
      ),
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const suffixColumnsWithConfig = getVisibleColumns(suffixColumns, columnsConfig);
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return accountDetails ? (
    <KwPanel className="mb-4">
      <header>
        <h2>{t('Keys')}</h2>
        {accountDetails.data.length > 0 && (
          <Button
            className="btn btn-danger btn-xs-full space-bottom-xs-s me-2"
            onClick={() => onDisableKeys(itemsChecked)}
            icon={icons.DISABLE_KEYS}
            text={t('Disable selected keys')}
            isDisabled={itemsChecked.length === 0}
          />
        )}
      </header>
      <Table
        columns={columns}
        suffixColumns={suffixColumnsWithConfig}
        allColumns={allColumns}
        rows={accountDetails.data.map((x: any) => ({ Key: x.id, ...x }))}
        totalRowCount={accountDetails.totalRowCount}
        filteredRowCount={accountDetails.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={(filters: any, scopes: any) =>
          exportAccountDetailKeyRequestsForAccount(id, filters, scopes)
        }
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}
export default AccountDetailKeyRequestsList;
